import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'sgh_cart_v1';

export function normalizeCartItem(item) {
  if (!item || typeof item !== 'object') return null;
  const productId = String(item.productId || '').trim();
  const name = String(item.name || '').trim().slice(0, 160);
  const slug = String(item.slug || '').trim().slice(0, 180);
  const image = String(item.image || '').trim().slice(0, 2048);
  const price = Number(item.price);
  const quantity = Math.max(1, Math.min(20, Math.trunc(Number(item.quantity) || 1)));
  if (!productId || !name || !slug || !Number.isFinite(price) || price < 0) return null;
  return { productId, name, slug, image, price, quantity };
}

function initialCart() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(value) ? value.slice(0, 30).map(normalizeCartItem).filter(Boolean) : [];
  } catch { return []; }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(initialCart);
  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);
  const add = (product, quantity = 1) => setItems((current) => {
    const next = normalizeCartItem({ productId: product?._id, name: product?.displayName || product?.name, slug: product?.slug, image: product?.productImage?.src || product?.image?.src || '', price: product?.offerPrice ?? product?.mrp?.amount, quantity });
    if (!next) return current;
    const existing = current.find((item) => item.productId === product._id);
    if (existing) return current.map((item) => item.productId === product._id ? { ...item, quantity: Math.min(20, item.quantity + next.quantity) } : item);
    if (current.length >= 30) return current;
    return [...current, next];
  });
  const update = (productId, quantity) => setItems((current) => current.map((item) => item.productId === productId ? { ...item, quantity: Math.max(1, Math.min(20, Number(quantity) || 1)) } : item));
  const remove = (productId) => setItems((current) => current.filter((item) => item.productId !== productId));
  const clear = () => setItems([]);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const estimatedSubtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const value = useMemo(() => ({ add, clear, estimatedSubtotal, itemCount, items, remove, update }), [estimatedSubtotal, itemCount, items]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() { const value = useContext(CartContext); if (!value) throw new Error('useCart must be used inside CartProvider.'); return value; }
